import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { Collapse } from 'antd';

import { DataState, Skeleton } from '../loading';

import useDateFormat from '../../hooks/useDateFormat';
import useEnsureCards from '../../hooks/useEnsureCards';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useSettledValue, { SELECTION_SETTLE_MS } from '../../hooks/useSettledValue';
import { pairRangeEntries } from '../../utils/dateBounds';
import { formatDisplayDate, formatDisplayDateTime, formatSourceDate } from '../../utils/dateDisplay';
import { groupElementsIntoPanels, isElementValueEmpty } from '../../utils/elements';
import { AUTO_PLACEMENT, formLayoutPanels, leftOutNames, placementOf } from '../../utils/formLayout';
import { cellSpans, mergedCellSpans } from '../../utils/formGridCells';
import { FormGrid, FormGridCell } from '../common/FormGrid';
import { sectionSummary } from '../../utils/sectionSummary';
import useFormLayout from '../../hooks/useFormLayout';
import { formatRelativeToNow } from '../../utils/formatters';
import { normalizeId } from '../../utils/ids';
import { cardChain, cardPagePath, commentsAllowedForChain } from '../../utils/cards';
import AttachmentsSection from '../common/AttachmentsSection';
import { canTakeCopies } from '../../utils/permissions';
import RecordBadge from '../common/RecordBadge';
import { cardOwnerOf } from '../cards/CardVisuals';
import UserRef from '../users/UserRef';
import HistoryList from '../history/HistoryList';
import { fetchItemHistory } from '../../services/history';
import { CHEVRON_RIGHT, HISTORY, INFO, PAPERCLIP, RELATE } from '../icons';
import ItemFieldRow from './ItemFieldRow';
import SectionHeaderStatus from './SectionHeaderStatus';
import { sourceCreatedAt } from './ItemAuditLine';
import ItemSourceLine from './ItemSourceLine';
import CopiedFromNote from './CopiedFromNote';
import RecordStateTags from './RecordStateTags';
import IntakeReviewBar from './IntakeReviewBar';
import IntakePrivatePanel from './IntakePrivatePanel';
import { isPendingIntake } from '../../services/records';
import { lockStateFor, recordPolicyFor, recordPolicyForChain } from '../../utils/recordPolicy';
import RelatedItemsSection from './RelatedItemsSection';
import ItemComments from './ItemComments';
import ItemTagEditor from '../tags/ItemTagEditor';
import ItemTagChips from '../tags/ItemTagChips';
import ItemOwnTagRow from '../tags/ItemOwnTagRow';
import { publicTagEntries } from '../../utils/itemTags';
import getItemDisplayValue from '../../utils/items';
import { fileElementVisible, isFileElement } from '../../utils/fileValues';
import { isRepeatingGroup } from '../../config/repeatingGroups';
import LinkedEntryNotice from './LinkedEntryNotice';
import useItemConfirmations from '../../hooks/useItemConfirmations';
import ExternalLink from '../common/ExternalLink';

// What an attachments or related-items section shows while the record on
// show is still moving (see `metaItemId`): the same skeleton the section
// shows while it loads.
const NO_FOLDS = Object.freeze({});

const MetaSettling = () => <Skeleton variant="line" count={2} />;

const MetaLabel = ({ icon: Icon, children, count }) => (
	<span className="item-read__meta-label">
		<Icon aria-hidden="true" />
		<span>{children}</span>
		{typeof count === 'number' ? (
			<span className="item-read__meta-count">{count}</span>
		) : null}
	</span>
);

/**
 * The item read view: the clean, portable display of one record.
 *
 * Renders everything the record IS — its fields (grouped into the same
 * panels the entry form uses), its tags (a chip row with "+ Add tag",
 * ItemTagEditor: private tags need only read access, so a reader tags too),
 * its state, provenance — and, when
 * `showMeta` is on, everything attached TO it behind a compact accordion:
 * attachments (upload/preview/download inline when opened), related items,
 * and the record's own facts (created, updated, creator, card, id). Nothing
 * here edits a field; the entry form is the door for that, and the surface
 * around this view (the detail pane, the inline mobile detail, the public
 * shared page) decides which doors to offer.
 *
 * The same component renders the desktop pane, the mobile inline detail, and
 * the public shared item, which is what keeps the three reading alike.
 *
 * The fields read in the card's FORM LAYOUT when it has one (its own or the
 * nearest ancestor's — utils/formLayout), exactly as the entry form renders
 * them: the layout's groups in its order, then "Other fields" for whatever
 * it does not place. `formLayout` undefined asks the API for it (cached per
 * card, useFormLayout); a caller that knows better passes the resolved
 * layout, or null for the automatic grouping — the public share page, which
 * has no session to ask with.
 */
export default function ItemReadView({
	item,
	card,
	elementDefs,
	resolveLookupValue,
	collectionId,
	collection = null,
	canEdit = false,
	showMeta = true,
	showTags = true,
	refreshKey = 0,
	defaultOpenKeys = [],
	className = '',
	// Measurement display: null = the viewer's preference; the public shared
	// page passes 'default' (the card's own rules).
	unitSystem = null,
	// Accept / Reject on an intake submission (IntakeReviewBar). Absent, the
	// bar still decides, and the surface simply does not hear about it.
	onIntakeReviewed = null,
	// undefined: fetch the card's form layout; null: group automatically; a
	// layout row (or its body): use it.
	formLayout: formLayoutProp,
	// May the reader take copies of the files (download, preview)? Undefined:
	// what `collection` says — a link-only reader of it only when the link
	// allows copies (utils/permissions canTakeCopies). A surface that knows
	// better (a linked row in another collection's list) passes it.
	canDownloadAttachments,
	// The Related items section. Off for a parent collection's record opened
	// in a sub-collection by reference: its own references open nothing.
	showRelated = true,
	// The tag row (docs/plans/tags.md §5.2). Off in the form layout preview.
	// `'public'` on the public share page (T7): the tags the page discloses,
	// READ-ONLY — no picker, no ✕, no links to tag pages, no private tags, no
	// "Why"; an id it serves no name for draws nothing (utils/itemTags.js
	// publicTagEntries). `publicCardsById` names an item tag's card there.
	// `onItemUpdated` hears every tag change (the list's feed).
	itemTags = true,
	publicCardsById = null,
	// The session's card cache, so the record policy is read through the
	// card's parents (a child card that states nothing inherits a parent's
	// lock). Absent, the card in hand is the chain.
	cardsById = null,
	onItemUpdated = null,
	// "Me too" (KPR-390): the toggle beside a confirmations element's count,
	// from one GET per item (hooks/useItemConfirmations). Off where nobody
	// signed in could press it — the public share page, the layout preview:
	// there the count reads alone.
	confirmations = true,
	// The Comments section (KPE-005, KPR-307), where Notes used to be: only
	// where a signed-in person reads the record in this session AND its card
	// allows them. Off by default: the public share page, the layout preview
	// and a parent record read by reference have no comments to show.
	comments = false,
}) {
	useDateFormat(); // re-render when the date-format preference changes
	useEnsureCards(item); // its card, if this session has not seen it yet
	const [counts, setCounts] = useState({ attachments: null, related: null, history: null });
	// History mounts lazily: the panel fetches only once opened (it is a
	// write-gated endpoint and most reads of a record never need it).
	const [historyOpened, setHistoryOpened] = useState(false);

	const itemId = normalizeId(item?._id);
	const cardId = normalizeId(item?.card_id || card?._id);
	const publicTags = useMemo(() => (itemTags === 'public' ? publicTagEntries(item) : []), [itemTags, item]);
	// The sections that ask the server about THIS record — attachments and
	// related items — are handed it once it has rested for
	// SELECTION_SETTLE_MS. The list's ↑/↓ swaps the record under a mounted
	// view at key-repeat speed; without the wait each step fired both
	// requests and the related items' lookup batch, for records gone before
	// they answered. The first record a view mounts with is settled at once.
	// Meanwhile the sections stay mounted on the record they last described,
	// hidden behind the skeleton they show while loading, so their counts
	// move from one record's straight to the next's.
	const settledItemId = useSettledValue(itemId, SELECTION_SETTLE_MS);
	const settling = settledItemId !== itemId;
	// The record the sections describe while the next one settles: the related
	// items' lookup pool keeps it, so a far-side row pointing back at it is
	// not fetched again on every step.
	const settledItemRef = useRef(item);
	if (!settling) settledItemRef.current = item;
	const whenSettled = section => (
		<>
			{settling ? <MetaSettling /> : null}
			<div hidden={settling}>{section}</div>
		</>
	);
	// A range reads as ONE row on the start's line ("Sep 9 – Sep 12 · 3 days");
	// the end element has no row of its own, wherever the card put it.
	// The form layout arrives after the fields can render; until it does the
	// automatic grouping stands in (a read view has no typing to disturb, and
	// a blank pane is worse than a re-ordered one — once per card per visit,
	// since the answer is cached).
	const { layout: fetchedLayout } = useFormLayout(cardId, { enabled: formLayoutProp === undefined });
	const layoutBody = formLayoutProp === undefined
		? fetchedLayout?.body
		: (formLayoutProp?.body || formLayoutProp || null);
	// A file element shows only while the reader passes the collection's
	// attachments gate (the read's `files` facts, else the switch itself):
	// with attachments off its values stay stored and the element is hidden
	// (docs/plans/data-types-file-element.md §5).
	const filesVisible = fileElementVisible({ item, collection });
	const shownDefs = useMemo(
		() => (filesVisible ? (elementDefs || []) : (elementDefs || []).filter(def => !isFileElement(def))),
		[elementDefs, filesVisible]
	);
	// An element the layout LEAVES OUT (KPR-1211) is drawn only while the
	// record holds a value for it — in the "Left out of the form" panel, never
	// "Other fields" (utils/formLayout formLayoutPanels).
	const itemElements = item?.elements;
	const panels = useMemo(() => {
		const leftOut = leftOutNames(layoutBody, shownDefs);
		const drawn = leftOut.size
			? shownDefs.filter(def => !leftOut.has(def?.name) || !isElementValueEmpty(def, itemElements?.[def.name]))
			: shownDefs;
		const slots = pairRangeEntries(drawn);
		const getElement = slot => slot.entry;
		return formLayoutPanels(slots, layoutBody, { getElement, surface: 'view' })
			|| groupElementsIntoPanels(slots, { getElement }).map(panel => ({ ...panel, description: null, collapsed: false }));
	}, [shownDefs, layoutBody, itemElements]);
	// Every headed section is a disclosure; the layout only says where it
	// starts. A person's own fold is not kept: it belongs to the record on
	// show, and the list's ↑/↓ swaps the record under this mounted view, so the
	// choices are filed under the record's id and a new record reads none
	// (KPR-581).
	const panelUid = `item-read-${useId().replace(/:/g, '')}`; // no colons in a DOM id (see ItemForm)
	const [folds, setFolds] = useState({ id: itemId, open: {} });
	const panelOpen = folds.id === itemId ? folds.open : NO_FOLDS;
	const setPanelOpen = useCallback(update => setFolds(prev => {
		const open = prev.id === itemId ? prev.open : {};
		return { id: itemId, open: typeof update === 'function' ? update(open) : update };
	}), [itemId]);
	const confirmable = useItemConfirmations({
		item,
		elementDefs: shownDefs,
		enabled: confirmations !== false,
		onItemUpdated,
	});

	// Where an entry's own link points: this item, in its collection. Not on
	// a public page (nothing there to open, and no collection to name).
	const entryLinks = useMemo(() => (
		itemTags !== 'public' && itemId && collectionId ? { collectionId: normalizeId(collectionId), itemId } : null
	), [itemTags, itemId, collectionId]);

	const createdRaw = item?.createdAt || item?.created_at;
	const updatedRaw = item?.updatedAt || item?.updated_at;
	const sourceCreated = sourceCreatedAt(item);
	const ownerId = normalizeId(item?.owner);
	const allowAttachments = collection?.allowAttachments === true;

	// What the card says about its records, read through its parents exactly
	// as the API reads it (RecordPolicy.assertWritable walks the chain): with
	// the card cache, the chain; without it, or when the cache does not hold
	// this card, the card in hand — the ⋯ menu's rule.
	// A surface that passes no cache reads the app's (KPR-155): a child card
	// inheriting `options.record` from its parent is Locked here too.
	const cardDefinitions = useOptionalCardDefinitions();
	const cardCache = cardsById || cardDefinitions?.cardsById || null;
	const recordPolicy = useMemo(
		() => (cardCache && cardId && cardCache[cardId]
			? recordPolicyFor(cardCache, cardId)
			: recordPolicyForChain([ card ])),
		[ cardCache, cardId, card ]
	);
	const recordState = lockStateFor(item, recordPolicy);

	// Do this card's items take comments? Read through its parents like every
	// card option (a child card states nothing and inherits; a global card is
	// the card in hand). The API still decides what the person may do in it.
	const commentsAllowed = useMemo(() => {
		if (!comments) return false;
		const chain = cardsById && cardId && cardsById[cardId]
			? cardChain(cardsById, cardId)
			: [card].filter(Boolean);
		return commentsAllowedForChain(chain);
	}, [comments, cardsById, cardId, card]);

	// The card (and so the list of fields) is still on its way: neither the card
	// nor a single element definition is in hand.
	const fieldsPending = !card && !(elementDefs && elementDefs.length);

	const metaItems = showMeta && itemId ? [
		allowAttachments ? {
			key: 'attachments',
			forceRender: true,
			label: <MetaLabel icon={PAPERCLIP} count={counts.attachments ?? undefined}>Attachments</MetaLabel>,
			children: whenSettled(
				<AttachmentsSection
					key={`attachments-${refreshKey}`}
					subjectType="item"
					subjectId={settledItemId}
					readOnly={!canEdit}
					locked={recordState.locked}
					canDownload={canDownloadAttachments ?? canTakeCopies(collection)}
					variant="embedded"
					onCountChange={n => setCounts(prev => (prev.attachments === n ? prev : { ...prev, attachments: n }))}
				/>
			),
		} : null,
		showRelated ? {
			key: 'related',
			forceRender: true,
			label: <MetaLabel icon={RELATE} count={counts.related ?? undefined}>Related items</MetaLabel>,
			children: whenSettled(
				<RelatedItemsSection
					itemId={settledItemId}
					collectionId={collectionId}
					// The record the section describes — not the one now on show
					// while it is still moving, whose lookups would be resolved
					// for the section of another.
					item={settledItemRef.current}
					disabled={!canEdit}
					variant="embedded"
					refreshKey={refreshKey}
					onCountChange={n => setCounts(prev => (prev.related === n ? prev : { ...prev, related: n }))}
				/>
			),
		} : null,
		canEdit ? {
			key: 'history',
			label: <MetaLabel icon={HISTORY} count={counts.history ?? undefined}>History</MetaLabel>,
			children: historyOpened ? (
				<HistoryList
					fetchPage={params => fetchItemHistory(itemId, params)}
					compact
					showTarget={false}
					pageSize={10}
					reloadKey={refreshKey}
					onCountChange={n => setCounts(prev => (prev.history === n ? prev : { ...prev, history: n }))}
				/>
			) : null,
		} : null,
		{
			key: 'info',
			label: <MetaLabel icon={INFO}>Info</MetaLabel>,
			children: (
				<dl className="item-read__info">
					<dt className="micro-label">Created</dt>
					<dd>
						{createdRaw ? (
							<>
								<span>{sourceCreated ? formatSourceDate(sourceCreated) : formatDisplayDateTime(createdRaw)}</span>
								<span className="item-read__relative">{formatRelativeToNow(sourceCreated || createdRaw)}</span>
							</>
						) : '—'}
					</dd>
					{/* An import's own date leads Created (KPR-1235); keepr's stays reachable. */}
					{sourceCreated && createdRaw ? (
						<>
							<dt className="micro-label">Added to keepr</dt>
							<dd>{formatDisplayDateTime(createdRaw)}</dd>
						</>
					) : null}
					<dt className="micro-label">Updated</dt>
					<dd>
						{updatedRaw ? (
							<>
								<span>{formatDisplayDateTime(updatedRaw)}</span>
								<span className="item-read__relative">{formatRelativeToNow(updatedRaw)}</span>
							</>
						) : '—'}
					</dd>
					<dt className="micro-label">Created by</dt>
					<dd>{ownerId ? <UserRef userId={ownerId} size={20} /> : '—'}</dd>
					<dt className="micro-label">Card</dt>
					<dd>
						{card ? (
							// A card owned by another collection (a parent's, a
							// lender's) is the qualified card badge.
							<RecordBadge
								kind="card"
								record={card}
								title={card?.name || 'Card'}
								size="xs"
								to={cardId ? cardPagePath(normalizeId(card?._id) === cardId ? card : cardId, collectionId) : undefined}
								collection={cardOwnerOf(card)}
							/>
						) : '—'}
					</dd>
					<dt className="micro-label">ID</dt>
					<dd><code className="item-read__id">{itemId}</code></dd>
					{/* Where an import came from lives here, not under the title (KPR-579). */}
					{item?.source?.system || item?.source?.externalId ? (
						<>
							{item.source.system ? (<><dt className="micro-label">Source system</dt><dd>{item.source.system}</dd></>) : null}
							{item.source.externalId ? (<><dt className="micro-label">External id</dt><dd><code className="item-read__id">{item.source.externalId}</code></dd></>) : null}
							{item.source.ingestedAt ? (<><dt className="micro-label">Imported</dt><dd>{formatDisplayDate(item.source.ingestedAt)}</dd></>) : null}
							{typeof item.source.ref === 'string' && item.source.ref.trim() ? (
								<>
									<dt className="micro-label">Source reference</dt>
									<dd>
										{/^https?:\/\//i.test(item.source.ref.trim())
											? <ExternalLink href={item.source.ref.trim()}>{item.source.ref.trim()}</ExternalLink>
											: item.source.ref.trim()}
									</dd>
								</>
							) : null}
						</>
					) : null}
				</dl>
			),
		},
	].filter(Boolean) : [];

	const classes = ['item-read', className].filter(Boolean).join(' ');

	return (
		<div className={classes}>
			{/* ABOVE the fields: where the values came from has to be read
			    before the values are. Nothing unless the item is a pending
			    submission and the reader can decide it. */}
			<IntakeReviewBar
				item={item}
				canEdit={canEdit}
				onReviewed={onIntakeReviewed}
				className="item-read__intake"
			/>
			{/* Beside the submission, never among its fields: what its visitor
			    sent privately (KPR-389), for the collection's managers alone. */}
			<IntakePrivatePanel item={item} collection={collection} elementDefs={elementDefs} className="item-read__intake" />

			{!fieldsPending ? <LinkedEntryNotice groupDefs={shownDefs} elements={item?.elements} className="item-read__linked-entry" /> : null}

			{fieldsPending ? (
				// The card definition has not arrived: its fields are unknown,
				// so say nothing about them yet — never "This record has no
				// fields".
				<DataState
					state="loading-initial"
					skeleton={<Skeleton variant="form-field" count={4} />}
					label="the record"
				/>
			) : panels.length === 0 ? (
				<div className="item-read__empty">This record has no fields.</div>
			) : panels.map((panel, index) => {
				// Every headed section is a disclosure (KPR-581); a lone
				// untitled panel has no heading, so nothing to fold.
				const headed = !!(panel.label || panels.length > 1 || panel.collapsed);
				const open = !headed || (panelOpen[panel.key] ?? !panel.collapsed);
				const bodyId = `${panelUid}-${index}`;
				// Closed, the heading says how much of it is filled and whether
				// a required field in it is still blank — what is not shown must
				// not hide that (utils/sectionSummary).
				const summary = headed && !open
					? sectionSummary(panel.entries.map(slot => ({ element: slot.entry, end: slot.end || null })), item?.elements)
					: null;
				return (
				<section className="item-read__panel" key={panel.key} data-panel={panel.key}>
					{headed ? (
						<button
							type="button"
							className="item-read__kicker-toggle"
							aria-expanded={open}
							aria-controls={open ? bodyId : undefined}
							onClick={() => setPanelOpen(prev => ({ ...prev, [panel.key]: !open }))}
						>
							<CHEVRON_RIGHT className="item-read__kicker-chevron" aria-hidden="true" />
							<span className="micro-label item-read__kicker">{panel.label || card?.name || 'Details'}</span>
							{summary ? <SectionHeaderStatus summary={summary} surface="view" /> : null}
						</button>
					) : null}
					{panel.description && open ? <p className="item-read__description">{panel.description}</p> : null}
					{open ? <FormGrid className="item-read__fields" id={bodyId}>
						{panel.entries.map((slot, index) => {
							// The same width the entry form draws this element at;
							// a range (one value here, two fields there) as wide as
							// its two halves together, up to a full row (KPR-626, k07).
							const { span, newRow } = panel.placements?.[index] || AUTO_PLACEMENT;
							// A repeating group is a list of entries: the whole row, as on the form.
							const cell = slot.end
								? mergedCellSpans(cellSpans(slot.entry, span), cellSpans(slot.end, placementOf(layoutBody, slot.end.name)?.span))
								: (isRepeatingGroup(slot.entry) && !Number.isInteger(span)
									? { ...cellSpans(slot.entry, span), wide: 12, medium: 6 }
									: cellSpans(slot.entry, span));
							return (
								<FormGridCell key={slot.entry?.name} className="item-read__cell" wide={cell.wide} medium={cell.medium} newRow={newRow}>
									<ItemFieldRow
										element={slot.entry}
										value={item?.elements?.[slot.entry?.name]}
										rangeEnd={slot.end}
										rangeEndValue={slot.end ? item?.elements?.[slot.end?.name] : undefined}
										resolveLookupValue={resolveLookupValue}
										unitSystem={unitSystem}
										item={item}
										canDownload={canDownloadAttachments ?? canTakeCopies(collection)}
										confirmation={confirmable.stateFor(slot.entry?.name)}
										onConfirmationToggle={confirmable.toggle}
										entryLinks={entryLinks}
										filesVisible={filesVisible}
									/>
								</FormGridCell>
							);
						})}
					</FormGrid> : null}
				</section>
				);
			})}

			{/* Where Notes was: the journal. The spec's read-only display of a
			    note left from before comments is not built — comments do not
			    deploy until every card's notes are resolved (KPE-005, decided
			    2026-10-08). Nothing in the wrapper, nothing drawn. */}
			{commentsAllowed && settledItemId ? (
				<div className="item-read__comments" hidden={settling} data-testid="item-read-comments">
					<ItemComments
						key={settledItemId}
						itemId={settledItemId}
						variant="read"
						refreshKey={refreshKey}
					/>
				</div>
			) : null}

			{/* A public page's tags: what it discloses, read-only (T7). */}
			{itemTags === 'public' && publicTags.length ? (
				<section className="item-read__tags-panel" data-testid="item-read-tags">
					<div className="micro-label item-read__kicker">Tags</div>
					<ItemTagChips entries={publicTags} label="Tags" cardsById={publicCardsById} />
				</section>
			) : null}

			{/* Its tags: the chips, then "+ Add tag" — saved as they are
			    picked (a read view has no Save). A saved item only. */}
			{itemTags && itemTags !== 'public' && itemId ? (
				<section className="item-read__tags-panel" data-testid="item-read-tags">
					<div className="micro-label item-read__kicker">Tags</div>
					<ItemTagEditor
						item={item}
						collection={collection}
						collectionId={collectionId}
						canEdit={canEdit}
						onItemUpdated={onItemUpdated}
					/>
					{/* Use as a tag / Stop using as a tag (T4d): a manager of
					    the item's collection, on a card in Chosen items mode —
					    nothing at all for anyone else. */}
					<ItemOwnTagRow
						item={item}
						card={card}
						title={getItemDisplayValue({ card, elementDefs, item, resolveLookupValue })}
						cardType={card?.label?.singular || card?.name || 'Item'}
						collection={collection}
						onItemUpdated={onItemUpdated}
					/>
				</section>
			) : null}

			{/* What this record is DOING — corrected, locked, briefly open —
			    where a reader is already looking. Discovering a lock by being
			    refused is the one way to make it confusing. */}
			{showTags && (recordState.locked || recordState.unlocked
				|| item?.supersededBy || isPendingIntake(item)) ? (
				<div className="item-read__tags item-pane__tags">
					<RecordStateTags item={item} lockState={recordState} />
				</div>
			) : null}

			{/* A submission through an intake link says so here; an import's
			    provenance lives in the Info section below (KPR-579). */}
			<ItemSourceLine intake={item?.intake} className="item-read__source" />
			{/* A copy made with Copy to… says where it came from (KPR-1024). */}
			<CopiedFromNote item={item} className="item-read__source" />

			{metaItems.length ? (
				<Collapse
					ghost
					size="small"
					className="item-read__meta"
					expandIconPosition="end"
					defaultActiveKey={defaultOpenKeys}
					onChange={keys => { if ((Array.isArray(keys) ? keys : [keys]).includes('history')) setHistoryOpened(true); }}
					items={metaItems}
				/>
			) : null}
		</div>
	);
}
